import styles from './PageHeader.module.scss';

import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Link, useRouteMatch} from 'react-router-dom';
import cx from 'classnames';
import {Navbar} from 'reactstrap';
import {ImageLogo} from 'components/Images';
import {useAuth} from 'contexts/AuthContext';
import PublicNav from './PublicNav';
import PrivateNav from './PrivateNav';
import throttle from 'lodash/throttle';
import useGetRoutes from 'hooks/useGetRoutes';
import {VIEWPORT_WIDTHS} from 'configs';
import {useViewport} from 'contexts/ViewportContext';
import {usePage, withPage} from 'contexts/PageContext';
import PageAlerts from './PageAlerts';
import GoogleTranslateElement from 'components/GoogleTranslateElement';
import {useHeader} from './useHeader';
import PageBestViewedAlert from './PageBestViewedAlert';

PageHeader.propTypes = {
  logoSize: PropTypes.oneOf(['md', 'lg']),
};

// google translate element + navbar
const HEADER_HEIGHT = 133;

PageHeader.defaultProps = {
  logoSize: 'md',
  dark: false,
  fluid: true,
  fixed: true,
  sticky: false,
  search: true,
  upgrade: true,
  hideMenu: false,
  utmProps: null,
};

function useSticky(isSticky) {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = throttle(() => {
      if (window.scrollY > HEADER_HEIGHT && !isScrolled) {
        setIsScrolled(true);
      } else if (window.scrollY <= HEADER_HEIGHT && isScrolled) {
        setIsScrolled(false);
      }
    }, 100);

    if (isSticky)
      window.addEventListener('scroll', handleScroll, {passive: true});

    return () => {
      if (isSticky) window.removeEventListener('scroll', handleScroll);
    };
  }, [isSticky, isScrolled]);

  return {isScrolled};
}

function PageHeader(props) {
  const {
    logoSize,
    dark,
    fluid,
    sticky,
    className,
    fixed,
    hideMenu,
    utmProps,
    ...rest
  } = props;
  const {isAuth} = useAuth();
  const {isScrolled} = useSticky(sticky);
  const {sidebar} = usePage();
  const {size} = useViewport();
  const {headerRef, headerHeight} = useHeader();

  // dark: light text on dark background
  // !dark: dark text on light background
  // isStickyMode: dark text on light background

  const isMobile = VIEWPORT_WIDTHS[size] < VIEWPORT_WIDTHS['xl'];
  const isStickyMode = sticky && isScrolled;
  // const _logoSize = isStickyMode || isAuth || isMobile ? 'md' : logoSize;
  let isDarkMode = dark;
  if (isStickyMode) isDarkMode = false;

  // if isStickyMode: light text on light background
  // if !isStickyMode: dark text on dark background

  const privateRoutes = useGetRoutes('private');
  const isPrivate = useRouteMatch(privateRoutes);

  const _className = cx(
    styles['header'],
    {
      [styles['header--light']]: !isPrivate,
    },
    {
      [styles['header--fluid']]: fluid,
      [styles['header--light']]: fixed || isStickyMode,
      [styles['header--fixed']]: fixed,
      [styles['header--sticky']]: isStickyMode,
      [styles['header--private']]: isPrivate && !isMobile && !!sidebar,
    },
    className
  );

  return (
    <React.Fragment>
      {/* So the navigation is not broken when scrolling */}
      {sticky && isScrolled && (
        <div className={styles['header--sticky-filler']} />
      )}
      <header style={{zIndex: 1035}} className={_className}>
        <div ref={headerRef} />
        <GoogleTranslateElement />

        <Navbar
          expand="xl"
          dark={isDarkMode}
          className={cx('container-fluid', styles['header-nav'])}
        >
          {(!isPrivate || !sidebar) && (
            <div className={cx({'flex-grow-1': hideMenu})}>
              <Link to="/">
                <ImageLogo size={'md'} />
                {/* temp to md, must have responsive sizes */}
              </Link>
            </div>
          )}

          {isAuth ? (
            <PrivateNav dark={isDarkMode} isMobile={isMobile} {...rest} />
          ) : (
            <PublicNav hideMenu={hideMenu} utmProps={utmProps} />
          )}
        </Navbar>
        <PageBestViewedAlert />
      </header>
      {isPrivate && <PageAlerts headerHeight={headerHeight} />}
    </React.Fragment>
  );
}

export default withPage(PageHeader);
